import { css } from '@emotion/react'
import { Heading, HFlow, Text, Theme, useTheme, VFlow } from 'bold-ui'
import { Box } from 'components/Box'
import { RadioGroupField, RadioGroupFieldProps, RadioOption, SimNaoEnum } from 'components/form'
import { ReactNode } from 'react'

const options: RadioOption<SimNaoEnum>[] = [
  {
    label: 'Sim',
    value: SimNaoEnum.SIM,
  },
  {
    label: 'Não',
    value: SimNaoEnum.NAO,
  },
]

export interface PerguntaRadioGroupBoxFieldListProps<T extends string>
  extends Omit<RadioGroupFieldProps<T>, 'label' | 'options'> {
  label: ReactNode
  description: ReactNode
  items: RadioOption<T>[]
  required?: boolean
  showItems?: boolean
}

export function PerguntaRadioGroupBoxListField<T extends string>(
  props: Readonly<PerguntaRadioGroupBoxFieldListProps<T>>
) {
  const { label, description, items, required, showItems = false, ...rest } = props

  const theme = useTheme()
  const styles = createStyles(theme, showItems)

  return (
    <Box style={styles.box}>
      <HFlow alignItems='center' justifyContent='space-between' style={styles.option}>
        <Text>
          <Heading level={4}>{label}</Heading>
          {required && <Text color='danger'> *</Text>}
        </Text>
        <RadioGroupField hSpacing={0.5} required={required} options={options} {...rest} />
      </HFlow>
      {showItems && (
        <VFlow vSpacing={0} style={styles.list}>
          <Heading level={5} css={styles.header}>
            {description}
          </Heading>
          {items.map((item) => (
            <HFlow key={item.value} alignItems='center' justifyContent='space-between' style={styles.listItem}>
              <Text>{item.label}</Text>
            </HFlow>
          ))}
        </VFlow>
      )}
    </Box>
  )
}

const createStyles = (theme: Theme, showItems: boolean) => ({
  box: css`
    padding: 0;
  `,
  option: css`
    padding: 0.75rem 1rem;
    ${showItems && `border-bottom: 1px solid ${theme.pallete.gray.c80};`}
  `,
  list: css`
    & > :not(:last-child) {
      border-bottom: 1px solid ${theme.pallete.gray.c80};
    }
  `,
  header: css`
    padding: 0.875rem 1rem;
  `,
  listItem: css`
    padding: 0.75rem 1rem;
  `,
})
